import { queryKeys } from '@query/keys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { filterDuplicatesById } from '@utils/duplicates'

import { TotalWalletBalanceDto } from '@model/TotalWalletBalanceDto'
import {
  CurrencyType,
  WalletCurrency,
  WalletDto,
  WalletStateEnum,
  WalletTypeEnum,
} from '@model/WalletDto'

const EMPTY_WALLETS: WalletDto[] = []
const EMPTY_CURRENCIES: WalletCurrency[] = []

interface UseWalletsPageDataProps {
  walletType: WalletTypeEnum
}

interface UseWalletsPageDataResult {
  wallets: WalletDto[]
  walletCurrencies: CurrencyType[]
  selectedWallet?: WalletDto
  selectWallet: (walletId: string) => void
  totalBalance?: TotalWalletBalanceDto
  availableCurrencies: WalletCurrency[]
  availableCurrencyIds: CurrencyType[]
  createWallet: (currency: WalletCurrency) => Promise<void>
  isWalletsLoading: boolean
  isTotalBalanceLoading: boolean
  isTotalBalanceError: boolean
  isAvailableCurrenciesLoading: boolean
  isAvailableCurrenciesError: boolean
  isCreatingWallet: boolean
  isError: boolean
}

export const useWalletsPageData = ({
  walletType,
}: UseWalletsPageDataProps): UseWalletsPageDataResult => {
  const { account } = useAccountReadContext()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const displayCurrency = account?.displayCurrency?.id ?? 'USD'

  const walletsQuery = useQuery({
    queryKey: queryKeys.wallet.byType(walletType),
    queryFn: () => apiClient.getWallets({ walletTypeId: walletType }),
    refetchOnMount: 'always',
  })

  const totalBalanceQuery = useQuery({
    queryKey: queryKeys.walletExtended.totalBalance(displayCurrency, walletType),
    queryFn: () => apiClient.getTotalBalanceByCurrency(displayCurrency, walletType),
    refetchOnMount: 'always',
  })

  const availableCurrenciesQuery = useQuery({
    queryKey: queryKeys.walletExtended.available(walletType),
    queryFn: () => apiClient.getAvailableWallets(walletType),
  })

  const { mutateAsync: createWalletRequest, isPending: isCreatingWallet } = useMutation({
    mutationFn: (currency: WalletCurrency) =>
      apiClient.addWallet({
        currencyId: currency.id,
        walletTypeId: walletType,
        walletStateId: WalletStateEnum.Active,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.wallet.root() }),
  })

  const wallets = walletsQuery.data ?? EMPTY_WALLETS
  const availableCurrencies = availableCurrenciesQuery.data ?? EMPTY_CURRENCIES
  const requestedWalletId = searchParams.get('walletId')

  const selectedWalletId = useMemo(() => {
    if (requestedWalletId && wallets.some((wallet) => wallet.id === requestedWalletId)) {
      return requestedWalletId
    }

    return wallets.find((wallet) => wallet.isDefault)?.id ?? wallets[0]?.id
  }, [requestedWalletId, wallets])

  const selectedWallet = useMemo(
    () => wallets.find((wallet) => wallet.id === selectedWalletId),
    [selectedWalletId, wallets]
  )

  const selectWallet = useCallback(
    (walletId: string) => {
      if (wallets.some((wallet) => wallet.id === walletId)) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.set('walletId', walletId)
            return next
          },
          { replace: true, state: location.state }
        )
      }
    },
    [wallets, setSearchParams, location.state]
  )

  const createWallet = useCallback(
    async (currency: WalletCurrency): Promise<void> => {
      await createWalletRequest(currency)
    },
    [createWalletRequest]
  )

  return {
    wallets,
    walletCurrencies: wallets
      .map((wallet) => wallet.currency)
      .filter(filterDuplicatesById)
      .map((currency) => currency.id),
    selectedWallet,
    selectWallet,
    totalBalance: totalBalanceQuery.data,
    availableCurrencies,
    availableCurrencyIds: availableCurrencies
      .filter(filterDuplicatesById)
      .map((currency) => currency.id),
    createWallet,
    isWalletsLoading: walletsQuery.isLoading,
    isTotalBalanceLoading: totalBalanceQuery.isLoading,
    isTotalBalanceError: totalBalanceQuery.isError && totalBalanceQuery.data === undefined,
    isAvailableCurrenciesLoading: availableCurrenciesQuery.isLoading,
    isAvailableCurrenciesError:
      availableCurrenciesQuery.isError && availableCurrenciesQuery.data === undefined,
    isCreatingWallet,
    isError: walletsQuery.isError && walletsQuery.data === undefined,
  }
}
